import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, Map, BarChart2, TrendingUp, Calendar, Package, Truck, 
  Warehouse, Globe, Route as RouteIcon, Clock, AlertTriangle, CheckCircle2, 
  Zap, Target, Users, DollarSign, ArrowUpRight, ArrowDownRight,
  RefreshCw, Settings, FileText, PieChart, LineChart, Scatter,
  Layers, Navigation, Satellite, Radio, Wifi, Signal
} from 'lucide-react';

// Import components
import OverviewTab from './components/OverviewTab';
import LogisticsTab from './components/LogisticsTab';
import InventoryTab from './components/InventoryTab';
import AnalyticsTab from './components/AnalyticsTab';
import NetworkTab from './components/NetworkTab';
import ReportsTab from './components/ReportsTab';
import SettingsTab from './components/SettingsTab';

const SCMSDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('7d');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity, color: 'from-blue-600 to-cyan-600' },
    { id: 'logistics', label: 'Logistics', icon: Truck, color: 'from-emerald-600 to-teal-600' },
    { id: 'inventory', label: 'Inventory', icon: Warehouse, color: 'from-purple-600 to-pink-600' },
    { id: 'analytics', label: 'Analytics', icon: BarChart2, color: 'from-orange-600 to-red-600' },
    { id: 'network', label: 'Network', icon: Globe, color: 'from-indigo-600 to-blue-600' },
    { id: 'reports', label: 'Reports', icon: FileText, color: 'from-gray-600 to-slate-600' },
    { id: 'settings', label: 'Settings', icon: Settings, color: 'from-slate-600 to-gray-600' }
  ];

  useEffect(() => {
    navigate(`/scms/${activeTab}`);
  }, [activeTab, navigate]);

  const handleRefresh = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className="space-y-6 p-6"
        >
          {/* Header */}
          <motion.div 
            className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 rounded-3xl p-8 text-white shadow-2xl"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.3),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(120,119,198,0.3),transparent_50%)]" />
            
            <div className="relative z-10">
              <div className="flex justify-between items-start">
                <div className="space-y-4">
                  <motion.div
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                    className="flex items-center space-x-4"
                  >
                    <div className="p-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                      <Package className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                        SCMS
                      </h1>
                      <p className="text-blue-100 text-lg font-medium">
                        Supply Chain Management System
                      </p>
                    </div>
                  </motion.div>
                  
                  <motion.p
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="text-blue-200 text-lg max-w-2xl"
                  >
                    Advanced supply chain orchestration with real-time tracking, predictive analytics, 
                    and intelligent automation for seamless operations.
                  </motion.p>
                </div>

                <motion.div
                  initial={{ x: 50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="flex items-center space-x-4"
                >
                  {/* Time Range Selector */}
                  <div className="relative">
                    <select
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value)}
                      className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm font-medium backdrop-blur-sm text-white appearance-none pr-10 focus:outline-none focus:ring-2 focus:ring-white/20"
                    >
                      <option value="24h">Last 24 Hours</option>
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                      <option value="90d">Last Quarter</option>
                      <option value="1y">Last Year</option>
                    </select>
                    <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70 pointer-events-none" />
                  </div>

                  {/* Refresh Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleRefresh}
                    className="p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-all duration-300"
                  >
                    <RefreshCw className={`w-5 h-5 text-white ${isLoading ? 'animate-spin' : ''}`} />
                  </motion.button>
                </motion.div>
              </div>

              {/* Animated Stats */}
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6"
              >
                {[
                  { label: 'Active Orders', value: '1,247', change: '+12.5%', icon: Package, color: 'from-blue-500 to-cyan-500' },
                  { label: 'In Transit', value: '89', change: '+8.2%', icon: Truck, color: 'from-emerald-500 to-teal-500' },
                  { label: 'Warehouses', value: '24', change: '+2.1%', icon: Warehouse, color: 'from-purple-500 to-pink-500' },
                  { label: 'Efficiency', value: '94.2%', change: '+3.8%', icon: Target, color: 'from-orange-500 to-red-500' }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-2 rounded-xl bg-gradient-to-r ${stat.color}`}>
                        <stat.icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex items-center text-green-300 text-sm">
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                        {stat.change}
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-blue-200 text-sm">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="inline-flex bg-white/80 backdrop-blur-xl rounded-2xl p-2 shadow-xl border border-white/20">
              {tabs.map((tab, index) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`relative flex items-center gap-3 px-6 py-4 rounded-xl text-sm font-medium transition-all duration-300 ${
                      isActive
                        ? 'text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl"
                        initial={false}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                    <div className="relative z-10 flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      <span>{tab.label}</span>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          {/* Content Area */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-white/20 overflow-hidden"
          >
            <Routes>
              <Route path="/" element={<OverviewTab timeRange={timeRange} />} />
              <Route path="/overview" element={<OverviewTab timeRange={timeRange} />} />
              <Route path="/logistics" element={<LogisticsTab timeRange={timeRange} />} />
              <Route path="/inventory" element={<InventoryTab timeRange={timeRange} />} />
              <Route path="/analytics" element={<AnalyticsTab timeRange={timeRange} />} />
              <Route path="/network" element={<NetworkTab timeRange={timeRange} />} />
              <Route path="/reports" element={<ReportsTab timeRange={timeRange} />} />
              <Route path="/settings" element={<SettingsTab />} />
            </Routes>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SCMSDashboard; 